import React from "react";
import { AluumeWordmark } from "@aluume/shared/components/brand/AluumeWordmark";
import { ElfsightInstagram } from "@/components/footer/ElfsightInstagram";
import { FlodeskSignup } from "@/components/footer/FlodeskSignup";

// Customer-facing site footer, aligned to the website Figma (file 20aQvq1rQJQ…,
// frame 186:2): Instagram carousel → wordmark + "Wellness redefined" + socials →
// Flodesk email signup. Sits on the cream surface.
const SOCIALS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/aluumewellness/",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/aluumewellness",
    icon: (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden>
        <path d="M14 9V7.5c0-.7.3-1 1-1h1.5V4h-2.2C12.1 4 11 5.2 11 7.2V9H9v2.5h2V20h3v-8.5h2.1l.4-2.5H14Z" />
      </svg>
    ),
  },
];

function FooterLink({ href, target, children }: { href: string; target?: string; children: React.ReactNode }) {
  return (
    <a href={href} target={target} rel={target === "_blank" ? "noopener noreferrer" : undefined} className="text-[14px] font-light text-ink transition-colors hover:text-accent">
      {children}
    </a>
  );
}

// `flushInstagramTop` drops the cream top padding + top rule above the Instagram
// carousel, for pages (e.g. /100) that end on a dark section and don't want a
// beige gap before the feed. Defaults off so every other page is unchanged.
export function SiteFooter({ flushInstagramTop = false }: { flushInstagramTop?: boolean } = {}) {
  return (
    <footer className="mt-auto w-full bg-bg text-ink">
      <div className={flushInstagramTop ? "pb-14" : "border-t border-rule py-14"}>
        <ElfsightInstagram />
      </div>

      {/* Brand block */}
      <div className="flex flex-col items-center gap-7 px-6 py-10 text-center">
        <AluumeWordmark className="h-12 w-auto text-ink md:h-14" />
        <p
          style={{ fontFamily: "var(--font-display)" }}
          className="text-[clamp(1.75rem,3vw,2.25rem)] font-light leading-none text-ink"
        >
          Wellness <span className="em">redefined</span>
        </p>
        <div className="flex items-center gap-4">
          {SOCIALS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              aria-label={s.label}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-12 w-12 items-center justify-center rounded-full bg-ink text-ink-on-dark transition-colors hover:bg-mocha"
            >
              {s.icon}
            </a>
          ))}
        </div>
      </div>

      {/* Flodesk email signup */}
      <div className="px-6 pb-16 md:px-12">
        <div className="mx-auto max-w-[960px]">
          <FlodeskSignup />
        </div>
      </div>

      {/* Nav links */}
      <div className="border-t border-rule px-6 py-12 md:px-12">
        <div className="mx-auto grid max-w-[960px] grid-cols-1 gap-10 sm:grid-cols-3">
          <div className="flex flex-col gap-3">
            <p className="text-[11px] uppercase tracking-[1.6px] text-ink-soft">Contact</p>
            <p className="text-[14px] font-light text-ink">Tel : <a href="tel:+6434412588" className="hover:text-accent transition-colors">+64 3 441 2588</a></p>
            <p className="text-[14px] font-light text-ink">Address : 34 Brecon Street, Queenstown, 9300</p>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-[11px] uppercase tracking-[1.6px] text-ink-soft">Aluume Experiences</p>
            <FooterLink href="/bathe">Bathe Hot Pools</FooterLink>
            {/* Temporary: Thermae points at the internal /100 founder page until Thermae
                bookings go live. Restore the marketing URL then:
                <FooterLink href="https://www.aluume.com/thermae-floating-sauna" target="_blank">Thermae Floating Sauna</FooterLink> */}
            <FooterLink href="/100">Thermae Floating Sauna</FooterLink>
            <FooterLink href="/vouchers">Gift Vouchers</FooterLink>
          </div>
          <div className="flex flex-col gap-3">
            <p className="text-[11px] uppercase tracking-[1.6px] text-ink-soft">Information</p>
            <FooterLink href="https://www.aluume.com/the-journal" target="_blank">The Journal</FooterLink>
            <FooterLink href="/terms">Terms &amp; Conditions</FooterLink>
            <FooterLink href="/manage">Manage Booking</FooterLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
